import {
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  useState,
} from 'react';
import styled from 'styled-components';

import { parseRelativeText } from 'dashboard/components/FormControlPilledInput/EditableItemTimeFramePicker/utils';
import { ControlConfigTimeFramePicker } from 'dashboard/components/FormControlPilledInput/types';
import { RangeValue, ValueType } from 'dashboard/models/graph-query';
import Sticker from 'design-system/components/Sticker/Sticker';
import { paragraphMixins } from 'design-system/mixins/text';

interface EditalbeItemTimeFramePickerProps {
  controlConfig: ControlConfigTimeFramePicker;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  value: RangeValue;
  onValueChange: (value: RangeValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  isCompact?: boolean;
}

const TimeFramePickerInputWrapper = styled('div')`
  display: inline-flex;
  align-items: center;
`;

const TimeFramePickerInput = styled('input')<{ isCompact: boolean }>`
  ${paragraphMixins.md};
  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  margin-left: 8px;
  background: none;
  border: none;

  min-width: 40px;
  height: ${({ isCompact }) => (isCompact ? '100%;' : '34px;')};
  display: block;
`;

// NOTE - This is antipattern. `placeholder` is usually passed via QueryConfig. However,
// for the operator `within` the value is determined by `rangeValueType`. In the future we can
// edit the the query config structure to dynamically calculate this.
const TYPE_TO_PLACEHOLDER: Partial<Record<ValueType, string>> = {
  [ValueType.DateTime]: 'e.g. last 12 hours',
  [ValueType.Date]: 'e.g. past 30 years',
};

const EditableItemTimeFramePicker = ({
  controlConfig,
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  onBlur,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditalbeItemTimeFramePickerProps) => {
  const [value, setValue] = useState<string>(initialValue['raw-value']);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const commitOrDeleteValue = () => {
    const trimmedValue = value.trim();

    if (trimmedValue === '') {
      if (isLast) {
        deleteLastItem();
      } else {
        deleteItem();
      }
    } else {
      const { periods, unit, errored } = parseRelativeText(trimmedValue);
      onValueChange({
        ...initialValue,
        'raw-value': trimmedValue,
        value: trimmedValue,
        'parsed-relative-time': {
          periods,
          unit,
        },
        errored: errored,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        range: undefined,
      });
    }
  };

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitOrDeleteValue();
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      commitOrDeleteValue();
    }
  };

  const handleBlur: FocusEventHandler = (e: FocusEvent<HTMLInputElement>) => {
    if (initialValue['raw-value'] !== value) {
      commitOrDeleteValue();
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  const { periods, unit, errored } = parseRelativeText(value);
  const stickerText = errored ? '-' : `${periods}${unit}`;

  const placeholder =
    controlConfig.rangeValueType in TYPE_TO_PLACEHOLDER
      ? TYPE_TO_PLACEHOLDER[controlConfig.rangeValueType]
      : '';

  return (
    <TimeFramePickerInputWrapper>
      {value && <Sticker>{stickerText}</Sticker>}
      <TimeFramePickerInput
        key={itemKey}
        isCompact={isCompact}
        ref={(input) => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef.current = input;
        }}
        size={value?.length || placeholder?.length || 3}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </TimeFramePickerInputWrapper>
  );
};

export default EditableItemTimeFramePicker;
